/**
 * The records behind a journey-row figure — who, not how many.
 *
 * The boxes on Arrive → Try → Buy → Stay → Leave answer "how many", and the
 * chart answers "when". Neither answers the question somebody actually opens the
 * dialog with, which is **which people** — a count of six cancelling trials is
 * not something you can act on until you know whose they are.
 *
 * ## Each metric sorts by its own date, and says which
 *
 * "Newest first" is not one field. A new account is newest by `createdAt`; an
 * expired subscription by the day it ended; a running trial by when it ends.
 * Firestore also forces the issue — the first `orderBy` must be the field the
 * range is on — so trying to impose one universal sort key would either need a
 * new composite index per metric or silently sort by something the reader is not
 * looking at.
 *
 * So every metric declares the date it is *about*, the list sorts on that
 * descending, and the column header names it. A reader is never left guessing
 * which date they are looking at.
 *
 * ## Cursor pagination, not offsets
 *
 * 25 a page, `startAfter` on the last document. Firestore charges for documents
 * it skips, so `offset` on page 40 of "New accounts, all time" would read
 * 1,000 documents to show 25. The dialog keeps the cursors it has been handed,
 * which is what makes Previous free.
 *
 * ## Two metrics need an in-memory filter, and it is why pages over-fetch
 *
 * `directSubs` is purchased-minus-promotional and `renewedSubs` needs a
 * field-to-field comparison Firestore cannot express, so both read a little more
 * than a page and drop what does not qualify. The cursor lands on the last row
 * actually shown, so no row is skipped or repeated across pages.
 */
import {
  collection,
  documentId,
  getDocs,
  limit as fsLimit,
  orderBy,
  query,
  startAfter,
  where,
  type DocumentData,
  type QueryConstraint,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { db, productDb } from '@/shared/firebase/client'
import { CRM_COLLECTIONS, PRODUCT_COLLECTIONS } from '@/shared/firebase/collections'
import { toIsoOrNull } from '@/shared/util/dates'
import type { MetricKey, Window } from './periodMetricsRepository'

export const METRIC_PAGE = 25

/** How many documents a page may read before giving up on filling itself. */
const OVER_FETCH = 4

export interface MetricRow {
  /** Stable React key — the document id, which is unique per collection. */
  key: string
  /** The account behind the row, where there is one. */
  uid: string
  name: string
  email: string
  /** The date this metric is about, ISO. Null where the record carries none. */
  at: string | null
  /** One short line of context: the product, the store, the price. */
  detail: string
}

export interface MetricPage {
  rows: MetricRow[]
  /** Pass back as `after` to get the next page. Null when this is the last. */
  cursor: QueryDocumentSnapshot<DocumentData> | null
  hasMore: boolean
  /**
   * Set when the rows are real but the names are not available.
   *
   * The subscription mirror holds `appUserId` and nothing else about a person;
   * names and addresses live in the product project, which needs its own
   * sign-in (decision 98). Rather than render a column of blanks, the dialog
   * says why and shows the account ids, which are still enough to find somebody.
   */
  identityNote: string | null
}

/**
 * Which date each metric is about — the sort key and the column header.
 *
 * `range: false` marks the two point-in-time metrics: they describe trials
 * running *now*, so there is no window to apply and the date shown is when the
 * trial ends. See `pointInTime` in `periodMetricsRepository`.
 */
const DATE_FIELD: Record<
  MetricKey,
  {
    field: string
    label: string
    range: boolean
    collection: 'subs' | 'users' | 'events' | 'subscribers' | 'appTrials'
  }
> = {
  newUsers: { field: 'createdAt', label: 'Signed up', range: true, collection: 'users' },
  newTrials: { field: 'startsAtMs', label: 'Trial started', range: true, collection: 'subs' },
  trialsConverting: { field: 'expiresAtMs', label: 'Trial ends', range: false, collection: 'subs' },
  trialsCancelling: { field: 'expiresAtMs', label: 'Trial ends', range: false, collection: 'subs' },
  directSubs: { field: 'startsAtMs', label: 'Started paying', range: true, collection: 'subs' },
  /**
   * People, not subscriptions — see `lostCustomers` in `periodMetricsRepository`.
   * The document id of `skidaCrmSubscribers` *is* the account id, so this list
   * resolves identities the same way New accounts does.
   */
  lostCustomers: {
    field: 'maxExpiresAtMs',
    label: 'Access ended',
    range: true,
    collection: 'subscribers',
  },
  renewedSubs: {
    field: 'currentPeriodStartsAtMs',
    label: 'Renewed',
    range: true,
    collection: 'subs',
  },
  convertedTrials: { field: 'eventAtMs', label: 'Converted', range: true, collection: 'events' },
  // Skida 3.0's own trial — the document id is the account id, as for subscribers.
  appTrialsRunning: { field: 'endsAtMs', label: 'Trial ends', range: false, collection: 'appTrials' },
  appTrialsConverted: { field: 'convertedAtMs', label: 'Bought', range: true, collection: 'appTrials' },
  appTrialsExpired: { field: 'endsAtMs', label: 'Trial ended', range: true, collection: 'appTrials' },
  cancelledTrials: { field: 'eventAtMs', label: 'Cancelled', range: true, collection: 'events' },
}

export const metricDateLabel = (key: MetricKey) => DATE_FIELD[key].label

const subs = () => collection(db, CRM_COLLECTIONS.subscriptions)
const subscribers = () => collection(db, CRM_COLLECTIONS.subscribers)
const events = () => collection(db, CRM_COLLECTIONS.subscriptionEvents)
const users = () => collection(productDb, PRODUCT_COLLECTIONS.users)
const appTrials = () => collection(db, 'skidaCrmAppTrials')

/** The equality filters that define each metric, beside its date range. */
function filtersFor(key: MetricKey, now: number): QueryConstraint[] {
  switch (key) {
    case 'appTrialsRunning':
      return [where('outcome', '==', 'running'), where('endsAtMs', '>', now)]
    case 'appTrialsConverted':
      return []
    case 'appTrialsExpired':
      return [where('outcome', '==', 'expired')]
    case 'newUsers':
      return []
    /**
     * A trial is `periodType == 'TRIAL'` — see the module header of
     * `periodMetricsRepository`. `status` was the filter here until 3 Sep 2026
     * and only the backfill ever writes it, so it stopped describing a
     * subscription the moment one converted.
     *
     * `newTrials` ranges on `startsAtMs` and orders by it, so its liveness test
     * cannot also be a filter — a second inequality would have to appear in the
     * ordering. It goes in `keeps()` instead, which is what that hook is for.
     */
    case 'newTrials':
      return [where('sandbox', '==', false), where('periodType', '==', 'TRIAL')]
    case 'trialsConverting':
      return [
        where('sandbox', '==', false),
        where('periodType', '==', 'TRIAL'),
        where('willRenew', '==', true),
        // Live *now*, which is the whole point of the metric — a period type
        // alone is what the mirror was told, an expiry compared to the clock is
        // a fact. These two order by `expiresAtMs`, so it is already the ranged
        // field and this composes.
        where('expiresAtMs', '>', now),
      ]
    case 'trialsCancelling':
      return [
        where('sandbox', '==', false),
        where('periodType', '==', 'TRIAL'),
        where('willRenew', '==', false),
        where('expiresAtMs', '>', now),
      ]
    case 'directSubs':
      // Purchased, minus promotional — the subtraction happens in memory below,
      // because `store != 'PROMOTIONAL'` is an inequality and the window
      // already spends the one range Firestore allows.
      return [where('sandbox', '==', false), where('ownership', '==', 'purchased')]
    case 'lostCustomers':
      return [where('sandbox', '==', false), where('status', '==', 'lapsed')]
    case 'renewedSubs':
      return [where('sandbox', '==', false)]
    case 'convertedTrials':
    case 'cancelledTrials':
      return [where('sandbox', '==', false)]
  }
}

/** Rows that survive the filters Firestore could not express. */
function keeps(key: MetricKey, d: QueryDocumentSnapshot<DocumentData>): boolean {
  if (key === 'newTrials') {
    // Still a trial, not merely one that started in the window: an EXPIRATION
    // event leaves `period_type: TRIAL` in place and moves only the expiry, so
    // the clock is the only thing that can tell a running trial from a lapsed
    // one. The count in `periodMetricsRepository` applies the same test as a
    // second range, which a list ordered by `startsAtMs` cannot.
    const expires = Number(d.get('expiresAtMs'))
    return Number.isFinite(expires) && expires > Date.now()
  }
  if (key === 'directSubs') {
    // Every promotional row is also `purchased` — RevenueCat means "not
    // family-shared" by that field, not "somebody paid".
    return String(d.get('store') ?? '') !== 'PROMOTIONAL'
  }
  if (key === 'renewedSubs') {
    // "Renewed" means the current period began later than the subscription did.
    const start = Number(d.get('startsAtMs'))
    const period = Number(d.get('currentPeriodStartsAtMs'))
    return Number.isFinite(start) && Number.isFinite(period) && period > start
  }
  /**
   * The two event metrics, filtered exactly as `countEvents` counts them — so
   * the list can never disagree with the figure above it.
   *
   * A conversion is whatever RevenueCat flagged `isTrialConversion`; a cancelled
   * trial is a CANCELLATION or EXPIRATION whose period was still TRIAL. Both are
   * in-memory because neither is expressible as a filter beside the date range.
   */
  if (key === 'convertedTrials') return d.get('isTrialConversion') === true
  if (key === 'cancelledTrials') {
    const type = String(d.get('type') ?? '')
    return (
      String(d.get('periodType') ?? '').toUpperCase() === 'TRIAL' &&
      (type === 'CANCELLATION' || type === 'EXPIRATION')
    )
  }
  return true
}

/** A date field that may be epoch millis or a Firestore timestamp. */
function dateOf(d: QueryDocumentSnapshot<DocumentData>, field: string): string | null {
  const v = d.get(field)
  if (typeof v === 'number' && Number.isFinite(v)) return new Date(v).toISOString()
  return toIsoOrNull(v)
}

function detailOf(key: MetricKey, d: QueryDocumentSnapshot<DocumentData>): string {
  const spec = DATE_FIELD[key]
  if (spec.collection === 'users') {
    const ents = d.get('activeEntitlements')
    return ents && typeof ents === 'object' && Object.keys(ents).length > 0 ? 'Premium' : 'Free'
  }
  if (spec.collection === 'events') {
    return String(d.get('type') ?? '')
  }
  if (spec.collection === 'appTrials') {
    const days = Number(d.get('days') ?? 0)
    return `${days}-day app trial · ${String(d.get('outcome') ?? '')}`
  }
  if (spec.collection === 'subscribers') {
    // What they had, since there is no single product to name: a departure may
    // close a chain of several renewal terms.
    const n = Number(d.get('subscriptionCount') ?? 0)
    return [
      `${n} subscription${n === 1 ? '' : 's'}`,
      d.get('everPaid') === true ? 'paid' : 'never paid',
    ].join(' · ')
  }
  const store = String(d.get('store') ?? '').replace('_', ' ').toLowerCase()
  const product = String(d.get('productId') ?? '')
  return [product, store].filter(Boolean).join(' · ')
}

/**
 * Names for a page of account ids, from the product project.
 *
 * One `in` query per 30 ids, so a 25-row page is a single read. A refusal here
 * is not an error: the product session is separate (98), and the page is still
 * useful with ids alone — so it resolves to an empty map and the caller says why.
 */
async function identities(
  uids: string[],
): Promise<{ byId: Map<string, { name: string; email: string }>; note: string | null }> {
  const ids = [...new Set(uids.filter(Boolean))]
  if (ids.length === 0) return { byId: new Map(), note: null }
  const byId = new Map<string, { name: string; email: string }>()
  try {
    const IN_CHUNK = 30
    for (let i = 0; i < ids.length; i += IN_CHUNK) {
      const snap = await getDocs(
        query(users(), where(documentId(), 'in', ids.slice(i, i + IN_CHUNK))),
      )
      for (const d of snap.docs) {
        byId.set(d.id, {
          name: String(d.get('name') ?? d.get('displayName') ?? ''),
          email: String(d.get('email') ?? ''),
        })
      }
    }
    return { byId, note: null }
  } catch {
    return {
      byId,
      note: 'Names come from the product project, which is not connected — showing account ids.',
    }
  }
}

export async function loadMetricRows(
  key: MetricKey,
  win: Window,
  now: number,
  after?: QueryDocumentSnapshot<DocumentData> | null,
): Promise<MetricPage> {
  const spec = DATE_FIELD[key]
  const ref =
    spec.collection === 'users'
      ? users()
      : spec.collection === 'events'
        ? events()
        : spec.collection === 'subscribers'
          ? subscribers()
          : spec.collection === 'appTrials'
            ? appTrials()
            : subs()

  const constraints: QueryConstraint[] = [...filtersFor(key, now)]

  if (spec.range) {
    /**
     * Epoch millis on every field, including the product users' `createdAt`.
     *
     * Checked rather than assumed, because guessing costs a silently empty list:
     * `countIn` — which produces the figure this list has to agree with — ranges
     * every field against raw `win.from`/`win.to`, and `toIsoOrNull` documents
     * `users.createdAt` as "epoch milliseconds (or seconds)". Comparing a number
     * field against a `Date` matches nothing and reports no error, so the list
     * would have come back empty for New accounts while the count above it read
     * 481.
     */
    // `lostCustomers` is also bounded by "already past", like its count is:
    // somebody whose access merely *will* run out has not left yet.
    const upper = key === 'lostCustomers' ? Math.min(win.to, now) : win.to
    if (win.from !== null) constraints.push(where(spec.field, '>=', win.from))
    constraints.push(where(spec.field, '<', upper))
  }

  constraints.push(orderBy(spec.field, 'desc'))
  if (after) constraints.push(startAfter(after))
  constraints.push(fsLimit(METRIC_PAGE * OVER_FETCH + 1))

  const snap = await getDocs(query(ref, ...constraints))
  const eligible = snap.docs.filter((d) => keeps(key, d))
  const page = eligible.slice(0, METRIC_PAGE)
  const last = page[page.length - 1]
  /**
   * More to come?
   *
   * Either the filter left spares beyond this page, or the read hit its limit
   * and there is more behind it. Both mean "there is a next page"; neither
   * claims to know how many.
   */
  const hasMore = eligible.length > page.length || snap.docs.length > METRIC_PAGE * OVER_FETCH

  const uids = page.map((d) =>
    spec.collection === 'users' || spec.collection === 'subscribers' || spec.collection === 'appTrials'
      ? d.id
      : String(d.get('appUserId') ?? ''),
  )
  const { byId, note } = await identities(uids)

  const rows: MetricRow[] = page.map((d, i) => {
    const uid = uids[i] ?? ''
    const who = byId.get(uid)
    return {
      key: d.id,
      uid,
      name: who?.name ?? '',
      email: who?.email ?? '',
      at: dateOf(d, spec.field),
      detail: detailOf(key, d),
    }
  })

  return { rows, cursor: last ?? null, hasMore, identityNote: note }
}
